import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Globe, ArrowLeft, Send, FileText, CheckCircle2, 
  ChevronRight, Copy, Download, Sparkles, Play,
  MessageCircle, Video, Lock, AlertTriangle
} from 'lucide-react';

const SECTION_ORDER = ['executive_summary', 'problem', 'solution', 'market', 'team', 'financials'];

const SECTION_META = {
  executive_summary: { name: 'Executive Summary', icon: '📋', color: 'from-blue-600 to-blue-800' },
  problem: { name: 'Problem Statement', icon: '🎯', color: 'from-red-600 to-red-800' },
  solution: { name: 'Solution', icon: '💡', color: 'from-amber-600 to-amber-800' },
  market: { name: 'Market Analysis', icon: '📊', color: 'from-purple-600 to-purple-800' },
  team: { name: 'Team', icon: '👥', color: 'from-emerald-600 to-emerald-800' },
  financials: { name: 'Financial Projections', icon: '💰', color: 'from-green-600 to-green-800' }
};

function BusinessPlanBuilder() {
  const auth = useAuth();
  const user = auth.user;
  const token = auth.token;
  
  const [currentSection, setCurrentSection] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState({});
  const [generating, setGenerating] = useState(false);
  const [ideaLocked, setIdeaLocked] = useState(false);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  async function handleStartSection(sectionKey) {
    setLoading(true);
    try {
      const response = await axios.post(API + '/ai/plan/start?section=' + sectionKey, {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setSessionId(response.data.session_id);
      setCurrentSection(sectionKey);
      setMessages([{
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Failed to start section:', error);
      toast.error('Failed to start section. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSendMessage() {
    if (!inputValue.trim() || loading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    setLoading(true);
    try {
      const response = await axios.post(API + '/ai/plan/chat', {
        session_id: sessionId,
        message: userMessage
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerateSection() {
    if (!sessionId || generating) return;
    
    setGenerating(true);
    try {
      const response = await axios.post(API + '/ai/plan/generate-section/' + sessionId, {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setGeneratedContent(prev => ({
        ...prev,
        [currentSection]: response.data.content
      }));
      
      toast.success('Section generated successfully!');
    } catch (error) {
      console.error('Failed to generate section:', error);
      toast.error('Failed to generate section. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  function handleCopyContent(content) {
    navigator.clipboard.writeText(content);
    toast.success('Content copied to clipboard!');
  }

  function handleExportPlan() {
    let fullPlan = '# INNOVATOR FOUNDER VISA - BUSINESS PLAN\n\n';
    fullPlan += 'Generated with innovatorfoundervisas.com AI\n\n';
    fullPlan += '---\n\n';
    
    for (let i = 0; i < SECTION_ORDER.length; i++) {
      const key = SECTION_ORDER[i];
      const meta = SECTION_META[key];
      const content = generatedContent[key];
      
      if (content) {
        fullPlan += '## ' + meta.name.toUpperCase() + '\n\n';
        fullPlan += content + '\n\n';
        fullPlan += '---\n\n';
      }
    }
    
    const blob = new Blob([fullPlan], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'innovator-visa-business-plan.md';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Business plan exported!');
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }

  function countCompletedSections() {
    let count = 0;
    for (let i = 0; i < SECTION_ORDER.length; i++) {
      if (generatedContent[SECTION_ORDER[i]]) {
        count++;
      }
    }
    return count;
  }

  const completedCount = countCompletedSections();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="business-plan-page">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#DC143C] opacity-5 blur-[150px] rounded-full" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#8B0000] opacity-5 blur-[150px] rounded-full" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-header">
        <div className="container-wide py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-[#71717A] hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold tracking-tight hidden sm:block">
                  <span className="text-white">Visa</span>
                  <span className="text-gradient-red">Founders</span>
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Badge className="badge-dark">
                {completedCount}/6 Sections
              </Badge>
              <Badge className="badge-red">
                <Sparkles className="w-3 h-3 mr-1" />
                GPT-5.2
              </Badge>
            </div>
          </div>
        </div>
      </nav>

      <main className="container-wide pt-28 pb-12 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            AI <span className="text-gradient-red">Business Plan</span> Builder
          </h1>
          <p className="text-[#A1A1AA] text-lg">
            Build your UK Innovator Visa business plan section by section with AI guidance
          </p>
        </motion.div>

        {/* Idea Lock Warning */}
        {!ideaLocked && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="card-dark border-yellow-500/20 bg-yellow-500/5 p-4 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-500 mb-1">Idea Not Locked Yet</p>
                  <p className="text-sm text-[#A1A1AA]">
                    Once you generate your first section, your idea will be locked and consume 1 credit. 
                    This cannot be changed afterwards. Make sure you have validated your idea in the VC Interview first.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Section List */}
          <div className="lg:col-span-1">
            <div className="card-dark rounded-2xl p-4 sticky top-24">
              <h2 className="font-bold mb-4 text-[#A1A1AA] text-sm uppercase tracking-wider">Plan Sections</h2>
              <SectionList 
                sections={SECTION_ORDER}
                meta={SECTION_META}
                currentSection={currentSection}
                generatedContent={generatedContent}
                loading={loading}
                onSelect={handleStartSection}
              />
              
              {/* Video Tutorial Block */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="card-dark rounded-xl p-4 bg-white/5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg gradient-red flex items-center justify-center">
                      <Video className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Video Tutorial</p>
                      <p className="text-xs text-[#71717A]">Watch & Learn</p>
                    </div>
                  </div>
                  <Button className="w-full btn-secondary text-sm" disabled>
                    <Play className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Chat Interface */}
          <div className="lg:col-span-2">
            <ChatInterface
              currentSection={currentSection}
              meta={SECTION_META}
              messages={messages}
              inputValue={inputValue}
              setInputValue={setInputValue}
              onSend={handleSendMessage}
              onKeyPress={handleKeyPress}
              onGenerate={handleGenerateSection}
              loading={loading}
              generating={generating}
              messagesEndRef={messagesEndRef}
            />
          </div>
        </div>

        {/* Generated Content */}
        <GeneratedContentSection
          sections={SECTION_ORDER}
          meta={SECTION_META}
          generatedContent={generatedContent}
          onCopy={handleCopyContent}
          onExport={handleExportPlan}
        />
        
        {/* Next Step CTA */}
        {completedCount >= 2 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 text-center"
          >
            <Link to="/tools/endorsement">
              <Button className="btn-primary glow-red px-8 py-6 text-lg" data-testid="plan-go-to-endorsement-btn">
                Score Against Endorsement Criteria
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        )}
      </main>

      {/* Support Button */}
      <button className="support-btn" data-testid="support-btn">
        <MessageCircle className="w-6 h-6 text-white" />
      </button>
    </div>
  );
}

function SectionList({ sections, meta, currentSection, generatedContent, loading, onSelect }) {
  const sectionCards = [];
  
  for (let i = 0; i < sections.length; i++) {
    const key = sections[i];
    const sectionMeta = meta[key];
    const isComplete = !!generatedContent[key];
    const isActive = currentSection === key;
    
    sectionCards.push(
      <button
        key={key}
        onClick={() => onSelect(key)}
        disabled={loading}
        className={'w-full text-left p-4 rounded-xl border transition-all mb-2 ' + 
          (isActive ? 'border-[#DC143C] bg-[#DC143C]/10' : 
           isComplete ? 'border-green-500/30 bg-green-500/5' : 
           'border-white/10 bg-white/5 hover:border-[#DC143C]/50')}
        data-testid={'plan-section-' + key}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={'w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ' + sectionMeta.color}>
              <span className="text-lg">{sectionMeta.icon}</span>
            </div>
            <div>
              <h3 className="font-semibold text-sm">{sectionMeta.name}</h3>
              <p className="text-xs text-[#71717A]">
                {isComplete ? 'Completed' : 'Click to start'}
              </p>
            </div>
          </div>
          {isComplete ? (
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#71717A]" />
          )}
        </div>
      </button>
    );
  }
  
  return <div>{sectionCards}</div>;
}

function ChatInterface({ currentSection, meta, messages, inputValue, setInputValue, onSend, onKeyPress, onGenerate, loading, generating, messagesEndRef }) {
  if (!currentSection) {
    return (
      <div className="card-dark rounded-2xl h-[600px] flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
            <FileText className="w-10 h-10 text-[#71717A]" />
          </div>
          <h3 className="text-xl font-bold mb-2">Select a Section</h3>
          <p className="text-[#71717A] max-w-sm">Choose a section from the left to start building your business plan with AI guidance</p>
        </div>
      </div>
    );
  }
  
  const sectionMeta = meta[currentSection];
  
  const messageElements = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const isUser = msg.role === 'user';
    messageElements.push(
      <motion.div 
        key={i}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={'flex ' + (isUser ? 'justify-end' : 'justify-start')}
        data-testid={'plan-message-' + i}
      >
        <div className={isUser ? 'chat-message-user' : 'chat-message-ai'}>
          <p className="whitespace-pre-wrap">{msg.content}</p>
        </div>
      </motion.div>
    );
  }
  
  return (
    <div className="chat-container rounded-2xl h-[600px] flex flex-col">
      {/* Header */}
      <div className={'px-6 py-4 bg-gradient-to-r ' + sectionMeta.color}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{sectionMeta.icon}</span>
            <h3 className="font-bold">{sectionMeta.name}</h3>
          </div>
          <Button
            onClick={onGenerate}
            disabled={generating || messages.length < 4}
            className="btn-secondary bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="plan-generate-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {generating ? 'Generating...' : 'Generate Section'}
          </Button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#0A0A0A]" data-testid="plan-messages-container">
        {messageElements}
        {loading && (
          <div className="flex justify-start">
            <div className="chat-message-ai">
              <div className="typing-indicator">
                <div className="typing-dot" />
                <div className="typing-dot" />
                <div className="typing-dot" />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <div className="chat-input-container p-4">
        <div className="flex gap-3">
          <Textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={onKeyPress}
            placeholder="Type your response..."
            className="chat-input flex-1 min-h-12 max-h-32 resize-none"
            disabled={loading}
            data-testid="plan-chat-input"
          />
          <Button
            onClick={onSend}
            disabled={loading || !inputValue.trim()}
            className="btn-primary px-6"
            data-testid="plan-send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function GeneratedContentSection({ sections, meta, generatedContent, onCopy, onExport }) {
  const contentCards = [];
  let hasContent = false;
  
  for (let i = 0; i < sections.length; i++) {
    const key = sections[i];
    const sectionMeta = meta[key];
    const content = generatedContent[key];
    
    if (content) {
      hasContent = true;
      contentCards.push(
        <div key={key} className="card-dark rounded-xl overflow-hidden" data-testid={'plan-content-' + key}>
          <div className={'px-4 py-3 bg-gradient-to-r ' + sectionMeta.color}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>{sectionMeta.icon}</span>
                <h3 className="font-semibold text-sm">{sectionMeta.name}</h3>
              </div>
              <Button
                onClick={() => onCopy(content)}
                variant="ghost"
                size="sm"
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="p-4 max-h-48 overflow-y-auto">
            <p className="text-sm text-[#A1A1AA] whitespace-pre-wrap">{content}</p>
          </div>
        </div>
      );
    }
  }
  
  if (!hasContent) return null;
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-12"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Generated Content</h2>
        {contentCards.length >= 2 && (
          <Button onClick={onExport} className="btn-primary" data-testid="plan-export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Plan
          </Button>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {contentCards}
      </div>
    </motion.div>
  );
}

export default BusinessPlanBuilder;
